/**
 * Turns a SKU's output JSON Schema into a readable shape: a tree of typed
 * fields (for a human-readable list) and a TypeScript `interface` string (for
 * the "what gets returned" code view). Handles the subset the AnyAPI output
 * schemas use: nested objects, arrays, enums, and `oneOf`/`anyOf` nullable
 * unions (e.g. `data: T | null`).
 */

export interface OutlineNode {
  name: string;
  /** Readable type label: `string`, `integer`, `object`, `object[]`, … */
  type: string;
  required: boolean;
  /** True when the value may also be `null` (a `oneOf [null, T]` branch). */
  nullable: boolean;
  /**
   * True for `x-anyapi-must-populate` fields: every provider attempts them,
   * so the value is empty only when the entity genuinely lacks the data.
   * Rendered as "when available" instead of "optional"/nothing.
   */
  mustPopulate: boolean;
  description?: string;
  /**
   * True when `type` is an array whose ELEMENTS may be `null` (distinct from
   * `nullable`, which is about the array itself). Positional batch SKUs return
   * one slot per requested input and put `null` in the slots that had no match,
   * so a caller must null-check each element.
   */
  itemNullable?: boolean;
  /** Nested fields when `type` is an object (or array of objects). */
  children?: OutlineNode[];
}

type Obj = Record<string, unknown>;

const asObject = (v: unknown): Obj | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;

interface Resolved {
  type: string;
  nullable: boolean;
  itemNullable?: boolean;
  children?: OutlineNode[];
}

/** Resolves one schema node to a type label, nullability, and nested fields. */
const resolve = (schema: unknown): Resolved => {
  const obj = asObject(schema);
  if (!obj) return { type: 'any', nullable: false };

  // Union: split the `null` branch out as nullability, resolve the rest.
  const union = (obj.oneOf ?? obj.anyOf) as unknown[] | undefined;
  if (Array.isArray(union)) {
    const branches = union.map(asObject).filter(Boolean) as Obj[];
    const nullable = branches.some((b) => b.type === 'null');
    const real = branches.filter((b) => b.type !== 'null');
    if (real.length === 1) {
      const r = resolve(real[0]);
      return { ...r, nullable: nullable || r.nullable };
    }
    return { type: real.map((b) => resolve(b).type).join(' | ') || 'any', nullable };
  }

  if (Array.isArray(obj.enum) && obj.enum.length > 0) {
    return { type: obj.enum.map((v) => JSON.stringify(v)).join(' | '), nullable: false };
  }

  const types = Array.isArray(obj.type) ? obj.type : obj.type ? [obj.type] : [];
  const nullable = types.includes('null');
  const t = types.find((x) => x !== 'null') as string | undefined;

  if (t === 'object' || obj.properties) {
    return { type: 'object', nullable, children: outlineProps(obj) };
  }
  if (t === 'array') {
    const item = resolve(obj.items);
    // An element that may be null has to reach the label and the generated
    // interface, or a customer copies a type its own responses violate.
    const label = item.nullable ? `(${item.type} | null)[]` : `${item.type}[]`;
    return { type: label, nullable, itemNullable: item.nullable, children: item.children };
  }
  return { type: t ?? 'any', nullable };
};

const outlineProps = (schema: Obj): OutlineNode[] => {
  const props = asObject(schema.properties);
  if (!props) return [];
  const required = new Set(Array.isArray(schema.required) ? (schema.required as string[]) : []);
  return Object.entries(props).map(([name, raw]): OutlineNode => {
    const r = resolve(raw);
    const node = asObject(raw);
    const desc = node?.description;
    return {
      name,
      type: r.type,
      required: required.has(name),
      nullable: r.nullable,
      mustPopulate: node?.['x-anyapi-must-populate'] === true,
      description: typeof desc === 'string' ? desc : undefined,
      itemNullable: r.itemNullable,
      children: r.children,
    };
  });
};

/** Top-level fields of an output schema (empty when it isn't an object shape). */
export const outlineSchema = (schema: unknown): OutlineNode[] => resolve(schema).children ?? [];

/** Maps JSON-Schema scalar names onto TypeScript types for the code view. */
const tsLeaf = (type: string): string =>
  type.replace(/\binteger\b/g, 'number').replace(/\bnull\b/g, 'null');

/** Renders the outline as a TypeScript `interface` declaration. */
export const toTypeScript = (nodes: OutlineNode[], rootName = 'Response'): string => {
  const lines: string[] = [];
  const render = (ns: OutlineNode[], depth: number) => {
    const pad = '  '.repeat(depth);
    for (const n of ns) {
      const opt = n.required ? '' : '?';
      const nul = n.nullable ? ' | null' : '';
      if (n.children) {
        const isArray = n.type.endsWith('[]');
        // A nullable element wraps the record so the union binds to the element,
        // not the array: `({ … } | null)[]`, never `{ … }[] | null`.
        const itemNul = isArray && n.itemNullable;
        lines.push(`${pad}${n.name}${opt}: ${itemNul ? '({' : '{'}`);
        render(n.children, depth + 1);
        const close = isArray ? (itemNul ? '} | null)[]' : '}[]') : '}';
        lines.push(`${pad}${close}${nul};`);
      } else {
        lines.push(`${pad}${n.name}${opt}: ${tsLeaf(n.type)}${nul};`);
      }
    }
  };
  lines.push(`interface ${rootName} {`);
  render(nodes, 1);
  lines.push('}');
  return lines.join('\n');
};
